import * as React from "react";
import { Form, Button, Grid, Divider } from "semantic-ui-react";
import { isEqual } from "lodash";

import { QuestionProps, FileAnswer, AnsweredQuestion, OhCaseDocumentFile } from "../../model";
import { FileUpload } from "../../../fileUploads";
import { CustomQuestionConfig, isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface FilesQuestionProps {
    api: string;
    onUpload?: (file: File) => Promise<string | OhCaseDocumentFile>;
    onDeleteFile?: (fileId: string, fileName: string, ohCaseDocumentId?: string) => Promise<void>;
    getDownloadLink?: (fileAnswer: FileAnswer) => string;
    createAdditionalFileItems?: (fileBlobId: string, fileName: string) => React.ReactNode;
    acceptExtensions?: string[];
}

class FilesQuestion extends React.Component<QuestionProps & FilesQuestionProps> {

    public componentDidUpdate(prevProps: QuestionProps) {
        if (!isEqual(prevProps.mandatory, this.props.mandatory)) {
            const answer = [...(this.props.answer as FileAnswer[] || [])];
            this.validateAndUpdateQuestion(answer);
        }
    }

    public render() {
        const { questionConfig, mandatory, showError, api, readonly, hideDivider } = this.props;
        const answers = this.props.answer as FileAnswer[] || [];
        if (isQuestionDisabled(questionConfig)) {
            return null;
        }

        return (
            <QuestionWrapper
                question={questionConfig.questionText}
                mandatory={mandatory}
                inline={this.props.inline}
                error={showError && mandatory && !answers && "This field must be completed"}
                hideDivider={hideDivider}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
            >
                <Form.Field>
                    {answers.map((answer, index) =>
                        (<FilesAnswerFileRow
                            key={answer.blobId || index}
                            readonly={readonly}
                            api={api}
                            onUpload={this.props.onUpload}
                            getDownloadLink={this.props.getDownloadLink}
                            createAdditionalFileItems={this.props.createAdditionalFileItems}
                            index={index}
                            answer={answer}
                            onChangeFile={this.changeFile}
                            onRemoveFile={this.removeFile}
                            acceptExtensions={this.props.acceptExtensions}
                        />)
                    )}
                    <div className="ui input">{!this.props.readonly && <Button type="button" content="Add" onClick={this.addDocument} />}</div>
                </Form.Field>
            </QuestionWrapper>
        );
    }

    private changeFile = (index: number, blobId: string, fileName: string, ohCaseDocumentId?: string) => {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const answer = [...(this.props.answer as FileAnswer[] || [])];
        answer[index] = { blobId, fileName, ohCaseDocumentId };
        this.updateQuestion({ question: this.props.questionConfig.questionText, answer, isValid: true });
    };

    private removeFile = (index: number) => {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const answer = [...(this.props.answer as FileAnswer[] || [])];
        const { blobId, fileName, ohCaseDocumentId } = answer[index];

        answer.splice(index, 1);

        this.updateQuestion({ question: this.props.questionConfig.questionText, answer, isValid: true });

        if (this.props.onDeleteFile && (blobId || ohCaseDocumentId)) {
            this.props.onDeleteFile(blobId, fileName, ohCaseDocumentId);
        }

        this.validateAndUpdateQuestion(answer);
    };

    private addDocument = () => {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const answer = [...(this.props.answer as FileAnswer[] || [])];
        answer.push({ blobId: "", fileName: "" });
        this.updateQuestion({ question: this.props.questionConfig.questionText, answer, isValid: true });
    };

    private validateAndUpdateQuestion(answer: FileAnswer[]) {
        if (!this.props.mandatory) {
            this.setQuestionValidity(answer, true);
        } else {
            const isAtLeastOneFileUploaded = answer.length > 0;
            this.setQuestionValidity(answer, isAtLeastOneFileUploaded);
        }
    }

    private setQuestionValidity(answer: FileAnswer[], valid: boolean) {
        const questionConfig = this.props.questionConfig as CustomQuestionConfig;
        this.updateQuestion({ question: questionConfig?.questionText, answer, isValid: valid });
    }

    private updateQuestion = (newQuestion: AnsweredQuestion) => {
        const { updateQuestion, onAnswerChange } = this.props;
        if (updateQuestion) {
            updateQuestion(newQuestion);
        }
        if (onAnswerChange) {
            onAnswerChange(newQuestion.answer);
        }
    };
}

interface FilesAnswerFileRowProps {
    readonly: boolean;
    api: string;
    answer: FileAnswer;
    index: number;
    onUpload?: (file: File) => Promise<string | OhCaseDocumentFile>;
    getDownloadLink?: (fileAnswer: FileAnswer) => string;
    createAdditionalFileItems?: (fileBlobId: string, fileName: string) => React.ReactNode;
    onChangeFile: (index: number, blobId: string, fileName: string, ohCaseDocumentId?: string) => void;
    onRemoveFile: (index: number) => void;
    acceptExtensions?: string[];
}

const FilesAnswerFileRow: React.FC<FilesAnswerFileRowProps> = (props: FilesAnswerFileRowProps) => {

    const { readonly, api, answer, index } = props;
    const [url, setUrl] = React.useState(null);

    React.useEffect(() => {
        (() => {
            if (readonly) {
                const downloadUrl = `${api}/${answer.blobId}?fileName=${answer.fileName}`;

                if (props.getDownloadLink) {
                    setUrl(props.getDownloadLink(answer) || downloadUrl);
                } else {
                    setUrl(downloadUrl);
                }
            }
        })();
    }, [answer, api, props, readonly]);

    const onRemoveClick = () => props.onRemoveFile(index);
    const onFileChange = (blobId: string, fileName: string, ohCaseDocumentId?: string) => props.onChangeFile(index, blobId, fileName, ohCaseDocumentId);

    if (readonly) {
        return (
            <Grid columns="2">
                <Grid.Column>
                    <a
                        href={url}
                        hidden={!answer.blobId}
                        download={answer.fileName}
                    >
                        <Button content={answer.fileName} type="button" loading={!url} />
                    </a>
                </Grid.Column>
                <Grid.Column>
                    {props.createAdditionalFileItems && props.createAdditionalFileItems(answer.blobId, answer.fileName)}
                </Grid.Column>
            </Grid>
        );
    }
    return (
        <>
            <Grid columns="3">
                <Grid.Column>
                    <FileUpload
                        apiUrl={api}
                        onUpload={props.onUpload}
                        onFileChange={onFileChange}
                        fileName={answer.fileName}
                        acceptExtensions={props.acceptExtensions}
                    />
                </Grid.Column>
                <Grid.Column textAlign="right" verticalAlign="middle">
                    <Button content="Remove" type="button" onClick={onRemoveClick} floated="right" />
                </Grid.Column>
                <Grid.Column>
                    {props.createAdditionalFileItems && props.createAdditionalFileItems(answer.blobId, answer.fileName)}
                </Grid.Column>
            </Grid>
            <Divider />
        </>);
};

export { FilesQuestion };
